/**
 * Per-site configuration read from the Drupal wrapper element.
 *
 * Both Drupal themes include the same built bundle, so anything that differs
 * per domain is declared on the wrapper instead of being hardcoded here:
 *
 *   <div id="search-base"
 *        data-site="hwsgr"
 *        data-active-filters="structure,location,days,time,special">
 *
 * - data-site: site key. Wählt den Block in config/siteConfig.json, in dem die
 *   domainspezifischen Beschriftungen stehen. Fehlt das Attribut => 'ibw'.
 * - data-active-filters: Komma-Liste der Filter, die gerendert werden.
 *   Fehlt das Attribut => alle Filter. Unbekannte Keys werden ignoriert.
 *
 * Read once at import time — the bundle's script tags come after the wrapper
 * markup, same as the data-location/data-structure read in index.js.
 */

import siteConfig from '../config/siteConfig.json';

export const FILTERS = {
  STRUCTURE: 'structure',
  LOCATION: 'location',
  DEGREE: 'degree',
  NIVEAU: 'niveau',
  EVENT_TYPE: 'event_type',
  DAYS: 'days',
  TIME: 'time',
  SPECIAL: 'special',
};

// Alle bekannten Keys — nur zur Validierung von data-active-filters.
const KNOWN_FILTERS = Object.keys(FILTERS).map((name) => FILTERS[name]);

// Ohne data-active-filters gelten diese Filter. Bewusst NICHT enthalten:
// FILTERS.NIVEAU und FILTERS.EVENT_TYPE — diese Filter müssen explizit
// aktiviert werden, sonst erscheinen sie auf jeder Site, sobald PerformX das
// Feld liefert (Angebotsart ist vorerst nur für HWSGR bestellt).
const OPT_IN_FILTERS = [FILTERS.NIVEAU, FILTERS.EVENT_TYPE];
const DEFAULT_FILTERS = KNOWN_FILTERS.filter((key) => OPT_IN_FILTERS.indexOf(key) === -1);

const getWrapper = () =>
  document.getElementById('search-base') || document.getElementById('search-inner');

const readAttribute = (name) => {
  const element = getWrapper();

  return (element && element.getAttribute(name)) || '';
};

const readActiveFilters = () => {
  const raw = readAttribute('data-active-filters');
  if (!raw) {
    return DEFAULT_FILTERS;
  }

  const requested = raw
    .split(',')
    .map((key) => key.trim().toLowerCase())
    .filter(Boolean);

  // Ignore unknown keys so a typo in the theme cannot blank the whole filter
  // list; an empty result would leave the search unusable.
  const known = requested.filter((key) => KNOWN_FILTERS.indexOf(key) !== -1);

  return known.length > 0 ? known : DEFAULT_FILTERS;
};

const site = readAttribute('data-site') || 'ibw';
const activeFilters = readActiveFilters();

export const getSite = () => site;

/**
 * Labels der aktiven Site: 'default' aus siteConfig.json, überschrieben von
 * den Werten unter dem Site-Key. Neue Beschriftungen dort ergänzen, nicht
 * hier im Code.
 */
const labels = {
  ...((siteConfig.default && siteConfig.default.labels) || {}),
  ...((siteConfig[site] && siteConfig[site].labels) || {}),
};

export const getLabel = (key) => labels[key] || '';

export const STRUCTURE_FILTER_LABEL = getLabel('structureFilter');

export const NIVEAU_FILTER_LABEL = getLabel('niveauFilter');

export const EVENT_TYPE_FILTER_LABEL = getLabel('eventTypeFilter');

export const getActiveFilters = () => activeFilters;

export const isFilterActive = (key) => activeFilters.indexOf(key) !== -1;
